<script lang="ts">
  import type { CheckReport, FixMode } from '@gostword/engine';
  import type { FixRequest, FixResponse } from '../lib/engine';
  import { countOf } from '../lib/format';
  import { inTelegram } from '../lib/telegram';
  import Icon from './Icon.svelte';

  let {
    report,
    onFix,
    onClose,
  }: { report: CheckReport; onFix: (r: FixRequest) => Promise<FixResponse>; onClose: () => void } = $props();

  const MODES: Array<{ id: FixMode; title: string; hint: string }> = [
    { id: 'tracked', title: 'Правки в режиме рецензирования', hint: 'Word покажет каждую правку — примите все сразу или по одной' },
    { id: 'clean', title: 'Исправить начисто', hint: 'Правки внесены без пометок' },
    { id: 'none', title: 'Только комментарии', hint: 'Текст не меняется, замечания — на полях документа' },
  ];

  let dialog: HTMLDialogElement;
  let mode = $state<FixMode>('tracked');
  let comments = $state(true);
  let excluded = $state<Record<string, boolean>>({});
  let busy = $state(false);
  let error = $state<string | null>(null);
  let done = $state<FixResponse | null>(null);
  // В Telegram файл не скачивается, а приходит в чат с ботом.
  const tg = inTelegram();

  const fixable = $derived(
    report.rules
      .filter((r) => r.status === 'fail')
      .map((rule) => ({ rule, count: report.findings.filter((f) => f.rule === rule.id && f.fix?.length).length }))
      .filter((x) => x.count > 0),
  );
  const included = $derived(fixable.filter((x) => !excluded[x.rule.id]).length);

  $effect(() => {
    dialog.showModal();
  });

  async function submit(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    busy = true;
    error = null;
    try {
      done = await onFix({
        mode,
        comments: mode === 'none' || comments,
        exclude: Object.keys(excluded).filter((k) => excluded[k]),
      });
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      busy = false;
    }
  }
</script>

<dialog
  class="modal"
  bind:this={dialog}
  onclose={onClose}
  onclick={(e) => {
    if (e.target === dialog) dialog.close();
  }}
>
  {#if done}
    <div class="done">
      <div class="done-icon"><Icon name="check" size={28} /></div>
      <h2>{done.delivered === 'sent' ? 'Файл отправлен в чат' : 'Файл сохранён'}</h2>
      <p>
        {#if mode === 'none'}
          Добавлено {countOf(done.commented, ['замечание', 'замечания', 'замечаний'])} на полях.
        {:else}
          Исправлено {countOf(done.fixed, ['замечание', 'замечания', 'замечаний'])}{#if done.commented}, ещё {done.commented} — в комментариях на полях{/if}.
        {/if}
      </p>
      {#if mode === 'tracked'}
        <p class="hint">В Word: «Рецензирование» → «Принять» → «Принять все исправления». Отдельные правки можно отклонить.</p>
      {/if}
      <button class="btn btn-primary" onclick={() => dialog.close()}>Готово</button>
    </div>
  {:else}
    <form onsubmit={submit}>
      <header class="dlg-head">
        <div>
          <h2>{tg ? 'Получить документ' : 'Скачать документ'}</h2>
          <p>
            {#if tg}
              Исходный файл не меняется. Документ исправляется на вашем устройстве, готовая копия придёт в чат с ботом.
            {:else}
              Исходный файл не меняется — вы получите копию. Всё происходит на вашем устройстве.
            {/if}
          </p>
        </div>
        <button type="button" class="icon-btn" aria-label="Закрыть" onclick={() => dialog.close()}><Icon name="x" /></button>
      </header>

      <div class="dlg-body">
        <div class="modes" role="radiogroup" aria-label="Как применить исправления">
          {#each MODES as m (m.id)}
            <label class="mode" class:active={mode === m.id}>
              <input type="radio" name="mode" value={m.id} bind:group={mode} />
              <span class="mode-text">
                <strong>{m.title}{#if m.id === 'tracked'}<span class="rec">рекомендуем</span>{/if}</strong>
                <small>{m.hint}</small>
              </span>
            </label>
          {/each}
        </div>

        {#if mode !== 'none'}
          <label class="check comments">
            <input type="checkbox" bind:checked={comments} />
            <span>Комментарии на полях к тому, что нельзя исправить автоматически</span>
          </label>
          {#if fixable.length}
            <details class="which">
              <summary>Что исправлять: {included} из {fixable.length}</summary>
              <ul>
                {#each fixable as { rule, count } (rule.id)}
                  <li>
                    <label class="check">
                      <input
                        type="checkbox"
                        checked={!excluded[rule.id]}
                        onchange={(e) => (excluded[rule.id] = !e.currentTarget.checked)}
                      />
                      <span class="rule-name">{rule.title}</span>
                      <span class="n">{count}</span>
                    </label>
                  </li>
                {/each}
              </ul>
            </details>
          {/if}
        {/if}

        {#if error}<p class="error" role="alert">{error}</p>{/if}
      </div>

      <footer class="dlg-foot">
        <button type="button" class="btn" onclick={() => dialog.close()}>Отмена</button>
        <button type="submit" class="btn btn-primary" disabled={busy}>
          <Icon name="download" size={18} />
          {busy ? 'Готовим файл…' : tg ? 'Отправить в чат' : 'Скачать .docx'}
        </button>
      </footer>
    </form>
  {/if}
</dialog>

<style>
  .modes {
    display: grid;
    gap: 8px;
  }

  .mode {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid var(--border);
    border-radius: 12px;
    cursor: pointer;
    transition:
      border-color 0.15s,
      background 0.15s;
  }

  .mode:hover {
    border-color: var(--border-strong);
  }

  .mode.active {
    border-color: var(--accent);
    background: var(--accent-soft);
  }

  .mode input {
    margin-top: 4px;
  }

  .mode-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .mode-text strong {
    font-weight: 600;
  }

  .mode-text small {
    color: var(--text-2);
    font-size: 13px;
  }

  .rec {
    margin-left: 8px;
    padding: 1px 7px;
    border-radius: 999px;
    background: var(--accent);
    color: var(--accent-text);
    font-size: 11px;
    font-weight: 650;
    vertical-align: 1px;
  }

  .comments {
    margin-top: 14px;
  }

  .which {
    margin-top: 12px;
    padding: 10px 12px;
    border: 1px solid var(--border);
    border-radius: 12px;
    font-size: 14px;
  }

  .which summary {
    font-weight: 560;
    cursor: pointer;
  }

  .which ul {
    display: grid;
    gap: 6px;
    margin: 10px 0 2px;
    padding: 0;
    list-style: none;
  }

  .rule-name {
    flex: 1;
  }

  .n {
    color: var(--text-3);
    font-variant-numeric: tabular-nums;
  }

  .error {
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: var(--error-soft);
    color: var(--error);
    font-size: 14px;
  }

  .done {
    padding: 32px 28px 26px;
    text-align: center;
  }

  .done-icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 14px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: var(--ok);
    color: #fff;
  }

  .done h2 {
    font-size: 22px;
  }

  .done p {
    margin-top: 8px;
    color: var(--text-2);
  }

  .done .hint {
    font-size: 13.5px;
  }

  .done .btn {
    margin-top: 20px;
    min-width: 140px;
  }
</style>
